import { useWorkoutStore } from "@/state/workout-store";
import { useSelectedCelebrity } from "@/features/celebrity";
import { getTimerLabel } from "../hooks/timer";
import { useCountUp } from "../hooks/use-count-up";

/**
 * Competitive HUD — single centered panel holding the round timer and scoreboard.
 * - Shows Solo HUD (no opponent) when no celebrity is selected OR when not in recording mode.
 * - Shows Full Competitive HUD (with opponent, centered VS icon, and ratio bar) during a recorded session with an opponent.
 */
export function CompetitiveHud({
  recording = false,
  roundEnded = false,
}: {
  recording?: boolean;
  /** The round clock has hit zero; the recording tail is running. */
  roundEnded?: boolean;
}) {
  const reps = useWorkoutStore((s) => s.reps);
  const celebrityReps = useWorkoutStore((s) => s.celebrityReps);
  const status = useWorkoutStore((s) => s.status);
  const elapsedMs = useWorkoutStore((s) => s.elapsedMs);
  const celebrity = useSelectedCelebrity();

  const animatedReps = useCountUp(reps);
  const animatedCelebrityReps = useCountUp(celebrityReps);

  if (status !== "active") return null;

  const roundDurationSec = celebrity?.workoutDurationSec ?? 0;
  const { label: timerLabel, low: timeLow } = getTimerLabel({
    roundDurationSec,
    elapsedMs,
    roundEnded,
  });

  // Solo mode or Non-recording mode: compact low-profile HUD with no opponent.
  if (!celebrity || !recording) {
    return (
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center px-2 pt-4 sm:px-4 sm:pt-6">
        <div className="w-auto">
          {/* Compact Glass Panel */}
          <div className="relative rounded-[20px] border border-white/15 bg-[#0a0f1d]/90 px-5 pb-2.5 pt-5 shadow-[0_15px_40px_rgba(0,0,0,0.85)] backdrop-blur-2xl transition-all duration-300">
            {/* Subtle Ambient Cyan Dot Grid Background Overlay */}
            <div
              className="pointer-events-none absolute inset-0 rounded-[20px] opacity-20"
              style={{
                backgroundImage: `radial-gradient(circle, #00a2ff 1.5px, transparent 1.5px)`,
                backgroundSize: "12px 12px",
                maskImage: "radial-gradient(circle, rgba(0,0,0,0.8), transparent)",
              }}
            />

            {/* Top Edge Glow Line */}
            <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#00a2ff] to-transparent opacity-80" />

            {/* Floating Top Timer Capsule */}
            <TimerChip label={timerLabel} low={timeLow} />

            {/* Compact Horizontal Content */}
            <div className="relative z-10 flex items-center justify-center gap-3">
              {/* Glowing Avatar Ring */}
              <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-[#00a2ff] bg-[#07111e] p-0.5 shadow-[0_0_12px_rgba(0,162,255,0.7),inset_0_0_8px_rgba(0,162,255,0.3)] sm:size-12">
                <div className="flex size-full items-center justify-center rounded-full bg-gradient-to-b from-[#0e1d33] to-[#050b14]">
                  <svg
                    viewBox="0 0 24 24"
                    className="size-5 text-white/90 sm:size-6"
                    fill="currentColor"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
              </div>

              {/* Score & Reps Stacked */}
              <div className="flex flex-col items-start leading-none">
                <span className="bg-gradient-to-b from-white via-slate-100 to-slate-400 bg-clip-text text-3xl font-black italic tabular-nums leading-none text-transparent drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] sm:text-4xl">
                  {animatedReps}
                </span>
                <span className="mt-0.5 text-[9px] font-black italic tracking-[0.2em] text-[#00a2ff] uppercase sm:text-[10px]">
                  REPS
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Competitive Mode (Celebrity selected AND Recording mode active): render full 2-player HUD
  const opponentName = celebrity.name;
  const formattedOpponentName =
    opponentName.toUpperCase().length > 12
      ? `${opponentName.toUpperCase().slice(0, 11)}...`
      : opponentName.toUpperCase();

  // Dual-color ratio bar logic: 50/50 initially (when total === 0), scaling dynamically with reps
  const total = reps + celebrityReps;
  const userPercent =
    total === 0 ? 50 : Math.min(95, Math.max(5, Math.round((reps / total) * 100)));

  return (
    <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center px-2 pt-4 sm:px-4 sm:pt-6">
      <div className="w-full max-w-[calc(100vw-1rem)] sm:max-w-4xl">
        {/* Main Glass Panel */}
        <div className="relative rounded-[20px] sm:rounded-[28px] border border-white/15 bg-[#0a0f1d]/90 px-3 pb-3 pt-5 sm:px-6 sm:pb-4 sm:pt-7 shadow-[0_25px_60px_rgba(0,0,0,0.85)] backdrop-blur-2xl transition-all duration-300">
          {/* Subtle Ambient Dot Grid Background Overlays */}
          <div
            className="pointer-events-none absolute inset-y-0 left-0 w-1/2 rounded-l-[20px] sm:rounded-l-[28px] opacity-20"
            style={{
              backgroundImage: `radial-gradient(circle, #00a2ff 1.5px, transparent 1.5px)`,
              backgroundSize: "14px 14px",
              maskImage: "linear-gradient(to right, rgba(0,0,0,0.8), transparent)",
            }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-1/2 rounded-r-[20px] sm:rounded-r-[28px] opacity-20"
            style={{
              backgroundImage: `radial-gradient(circle, #ff2a4b 1.5px, transparent 1.5px)`,
              backgroundSize: "14px 14px",
              maskImage: "linear-gradient(to left, rgba(0,0,0,0.8), transparent)",
            }}
          />

          {/* Top Edge Glow Lines */}
          <div className="absolute -top-px left-6 right-1/2 h-px bg-gradient-to-r from-transparent via-[#00a2ff] to-transparent opacity-80" />
          <div className="absolute -top-px left-1/2 right-6 h-px bg-gradient-to-r from-transparent via-[#ff2a4b] to-transparent opacity-80" />

          {/* Floating Top Timer Capsule */}
          <TimerChip label={timerLabel} low={timeLow} />

          {/* Main Scoreboard Area with 100% Centered VS Icon */}
          <div className="relative z-10 grid w-full grid-cols-2 items-center">
            {/* Left Side: YOU / User */}
            <div className="flex min-w-0 items-center justify-start gap-1.5 pr-4 sm:gap-3 sm:pr-8 md:gap-4">
              {/* Glowing Avatar Ring */}
              <div className="relative flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-[#00a2ff] bg-[#07111e] p-0.5 shadow-[0_0_12px_rgba(0,162,255,0.7),inset_0_0_8px_rgba(0,162,255,0.3)] sm:size-14 md:size-18">
                <div className="flex size-full items-center justify-center rounded-full bg-gradient-to-b from-[#0e1d33] to-[#050b14]">
                  <svg
                    viewBox="0 0 24 24"
                    className="size-5 text-white/90 sm:size-7 md:size-9"
                    fill="currentColor"
                  >
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
              </div>

              {/* Name & Rep Counter */}
              <div className="flex min-w-0 flex-col items-start leading-none">
                <span className="text-[10px] font-black italic tracking-wider text-[#00a2ff] uppercase sm:text-xs md:text-sm">
                  YOU
                </span>
                <span className="mt-0.5 bg-gradient-to-b from-white via-slate-100 to-slate-400 bg-clip-text text-3xl font-black italic tabular-nums leading-none text-transparent drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] sm:text-4xl md:text-6xl">
                  {animatedReps}
                </span>
              </div>
            </div>

            {/* 100% Dead-Centered VS Graphic Anchor */}
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 shrink-0 items-center justify-center">
              <img
                src="/ic-vs.png"
                alt="VS"
                className="h-6 w-auto object-contain drop-shadow-[0_0_10px_rgba(255,255,255,0.3)] sm:h-9 md:h-12"
              />
            </div>

            {/* Right Side: Opponent / Celebrity */}
            <div className="flex min-w-0 items-center justify-end gap-1.5 pl-4 sm:gap-3 sm:pl-8 md:gap-4">
              {/* Name & Rep Counter */}
              <div className="flex min-w-0 flex-col items-end text-right leading-none">
                <span className="max-w-[70px] truncate text-[10px] font-black italic tracking-wider text-[#ff2a4b] uppercase sm:max-w-[130px] md:max-w-[170px] sm:text-xs md:text-sm">
                  {formattedOpponentName}
                </span>
                <span className="mt-0.5 bg-gradient-to-b from-white via-slate-100 to-slate-400 bg-clip-text text-3xl font-black italic tabular-nums leading-none text-transparent drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] sm:text-4xl md:text-6xl">
                  {animatedCelebrityReps}
                </span>
              </div>

              {/* Glowing Avatar Ring */}
              <div className="relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[#ff2a4b] bg-[#1e070c] p-0.5 shadow-[0_0_12px_rgba(255,42,75,0.7),inset_0_0_8px_rgba(255,42,75,0.3)] sm:size-14 md:size-18">
                {celebrity?.imageDataUrl ? (
                  <img
                    src={celebrity.imageDataUrl}
                    alt={opponentName}
                    className="size-full rounded-full object-cover"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center rounded-full bg-gradient-to-b from-[#2e0e14] to-[#0e0406]">
                    <span className="text-sm font-bold text-white/90 sm:text-lg md:text-2xl">
                      {opponentName.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dual-Color Ratio Progress Bar (Blue YOU vs Red Opponent) */}
          <div className="relative mt-2.5 sm:mt-4 flex h-2.5 sm:h-3.5 md:h-4 w-full overflow-hidden rounded-full border border-white/15 bg-[#04070d] shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)]">
            {/* Subtle Diagonal Stripe Track Background */}
            <div
              className="pointer-events-none absolute inset-0 z-0 opacity-15"
              style={{
                backgroundImage: `repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 8px, transparent 8px, transparent 16px)`,
              }}
            />

            {/* Left Side: Blue Bar (YOU) */}
            <div
              className="relative z-10 h-full bg-gradient-to-r from-[#0055ff] via-[#00a2ff] to-[#00d4ff] transition-[width] duration-700 ease-out shadow-[0_0_12px_rgba(0,162,255,0.8)]"
              style={{ width: `${userPercent}%` }}
            >
              {/* Glossy Top Edge Highlight */}
              <div className="absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-white/40 to-transparent" />
            </div>

            {/* Right Side: Red Bar (Opponent) */}
            <div
              className="relative z-10 h-full flex-1 bg-gradient-to-r from-[#ff2a4b] via-[#e60033] to-[#a30021] transition-all duration-700 ease-out shadow-[0_0_12px_rgba(255,42,75,0.8)]"
              style={{ width: `${100 - userPercent}%` }}
            >
              {/* Glossy Top Edge Highlight */}
              <div className="absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-white/40 to-transparent" />
            </div>

            {/* Glowing White Boundary Divider */}
            <div
              className="pointer-events-none absolute top-0 bottom-0 z-20 w-1 -ml-0.5 bg-white shadow-[0_0_8px_#ffffff] transition-[left] duration-700 ease-out"
              style={{ left: `${userPercent}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TimerChip({ label, low }: { label: string; low: boolean }) {
  return (
    <div className="absolute -top-3.5 sm:-top-5 left-1/2 z-30 -translate-x-1/2">
      {/* Container with Dual Glow & Pill Shape */}
      <div
        className={`relative flex items-center justify-center rounded-full px-3 py-0.5 sm:px-5 sm:py-1.5 min-w-[100px] sm:min-w-[130px] border bg-[#04070d]/95 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.9)] ${
          low ? "border-red-500/80 shadow-[0_0_20px_rgba(255,42,75,0.7)]" : "border-white/15"
        }`}
        style={
          !low
            ? {
                boxShadow:
                  "-6px 0 12px -2px rgba(0, 162, 255, 0.6), 6px 0 12px -2px rgba(255, 42, 75, 0.6), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
              }
            : undefined
        }
      >
        <span
          className={`font-mono text-xs sm:text-base md:text-xl font-black italic tracking-widest tabular-nums ${
            low ? "text-red-400" : "text-white"
          } drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]`}
        >
          {label}
        </span>
      </div>
    </div>
  );
}
